import type {
  InvestorCoverageRead,
  PaginationMetadata,
} from '@capital-markets-gateway/api-client-rolodex';
import type { apiTypes } from '@cmg/common';
import isEmpty from 'lodash/isEmpty';
import React from 'react';

import type { InvestorCoverageFilters } from '../../../../../common/api/rolodexApiClient';
import type { AccountType } from '../../../../../types/domain/account/constants';
import type {
  Profile,
  ProfileUpdate,
  XcEmailSubscriptions,
} from '../../../../../types/domain/self/profile';
import { InvestorCoverageSection } from '../../../../design-system/shared/investor-coverage/InvestorCoverageSection';
import type { XcNotificationSettingPartsFragment } from './graphql/__generated__';
import { HangfireLinksSection } from './HangfireLinksSection';
import { LoginDetailsSection } from './LoginDetailsSection';
import { ProfileDetailsSection } from './ProfileDetailsSection';
import { XcEmailSubscriptionsGridSection } from './XcEmailSubscriptionsGridSection';

export type Props = {
  oidcUserAccountType: AccountType | 'SYSTEM' | null;
  profile: Profile | null;
  isProfileSubmitting: boolean;
  onProfileSubmit: (data: ProfileUpdate) => void;
  profileSubmitError: apiTypes.GenericServerError | null;
  oidcUserIdp: string | null;
  showInvestorCoverage: boolean;
  showHangfireLinks: boolean;
  isLoadingInvestorCoverage: boolean;
  investorCoverage: InvestorCoverageRead[];
  investorCoveragePagination: PaginationMetadata | null;
  investorCoverageFilters: InvestorCoverageFilters;
  onInvestorCoverageChangePage: (params: apiTypes.ListParams) => void;
  onInvestorCoverageChangeFilters: (params: InvestorCoverageFilters, debounce?: boolean) => void;
  xcEmailInitialValues: XcEmailSubscriptions;
  xcNotifications: readonly XcNotificationSettingPartsFragment[];
  isSavingXcEmail: boolean;
  onSaveXcEmail: (values: XcEmailSubscriptions) => Promise<void> | void;
};

export const ProfileGridContent: React.VFC<Props> = ({
  profile,
  isProfileSubmitting,
  onProfileSubmit,
  profileSubmitError,
  oidcUserIdp,
  oidcUserAccountType,
  showInvestorCoverage,
  showHangfireLinks,
  isLoadingInvestorCoverage,
  investorCoverage,
  investorCoveragePagination,
  investorCoverageFilters,
  onInvestorCoverageChangePage,
  onInvestorCoverageChangeFilters,
  xcEmailInitialValues,
  xcNotifications,
  isSavingXcEmail,
  onSaveXcEmail,
}) => {
  return (
    <React.Fragment>
      {profile && oidcUserIdp && (
        <ProfileDetailsSection
          submitError={profileSubmitError}
          isSubmitting={isProfileSubmitting}
          oidcUserAccountType={oidcUserAccountType}
          profile={profile}
          onSubmit={onProfileSubmit}
        />
      )}
      {oidcUserIdp === 'local' && <LoginDetailsSection />}
      {oidcUserIdp && !isEmpty(xcNotifications) && (
        <XcEmailSubscriptionsGridSection
          initialValues={xcEmailInitialValues}
          xcNotifications={xcNotifications}
          loading={isSavingXcEmail}
          onSubmit={onSaveXcEmail}
          canEdit={true}
        />
      )}
      {showInvestorCoverage && investorCoveragePagination && (
        <InvestorCoverageSection
          isLoading={isLoadingInvestorCoverage}
          investorCoverage={investorCoverage}
          pagination={investorCoveragePagination}
          filters={investorCoverageFilters}
          onChangeFilters={onInvestorCoverageChangeFilters}
          onChangePage={onInvestorCoverageChangePage}
        />
      )}
      {showHangfireLinks && <HangfireLinksSection />}
    </React.Fragment>
  );
};
